import { computed, getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { CallTarget } from '@/api/openapi';
import { key } from '@/store/index';
import { isLeadSynced } from '@/utils/salesforce';
import { useAvailableFeatureCheck } from './available-feature-check';
import { useAlert } from './message-dialog';

const useSfFeature = (modalName: string, getIsSfSyncValue: () => boolean) => {
  const { isAvailableSalesforceApiFeature } = useAvailableFeatureCheck();
  const isSfSync = computed<boolean>(() => getIsSfSyncValue() && isAvailableSalesforceApiFeature.value);
  const app = getCurrentInstance();
  const handleOpenModal = () => {
    app.appContext.config.globalProperties.$modal.show(modalName);
  };

  /**
   * @return boolean SF連携モーダルを開いたかどうか
   */
  const openSfModalIfNecessary = () => {
    if (isSfSync.value) {
      handleOpenModal();
      return true;
    }
    return false;
  };

  return {
    openSfModalIfNecessary,
    isSfSync,
  };
};

const useSfSync = (getCallTarget: () => CallTarget | null) => {
  const { alert } = useAlert();
  const i18n = useI18n();

  const isLeadSfSync = computed<boolean>(() =>
    isLeadSynced(getCallTarget()),
  );

  const canOpenCallResultFrom = computed<boolean>(() => {
    const sfSyncSettingInfo = getCallTarget()?.sfSyncSettingInfo;
    return sfSyncSettingInfo?.callResultSfSync === true && sfSyncSettingInfo?.sfUserId != null
      || sfSyncSettingInfo?.callResultSfSync !== true;
  });

  const validateCallResultSyncState = async () => {
    if (!canOpenCallResultFrom.value) {
      await alert({
        title: i18n.t('callTarget.errors.salesforceUnableToLinkUser.title'),
        message: i18n.t('callTarget.errors.salesforceUnableToLinkUser.text'),
      });
      return false;
    }
    return true;
  };

  return {
    isLeadSfSync,
    canOpenCallResultFrom,
    validateCallResultSyncState,
  };
};

type UseSfTaskSyncArgs = {
  isEdit: () => boolean;
  checkedIsSfSync: () => boolean;
};

/**
 * SFタスク同期チェックボックス（SyncCheckbox）の表示/活性制御ロジック。
 * @param isEdit 編集モードかどうかを返すgetter（consumerのprops/refを渡す）
 * @param checkedIsSfSync チェックボックスの選択状態を返すgetter
 */
const useSfTaskSync = ({ isEdit, checkedIsSfSync }: UseSfTaskSyncArgs) => {
  const store = useStore(key);
  const i18n = useI18n();
  const { isAvailableSalesforceApiFeature } = useAvailableFeatureCheck();
  const callTarget = computed<CallTarget | null>(() => store.getters['user/callTarget']);
  const { isLeadSfSync } = useSfSync(() => callTarget.value);

  const enabledInputSfSync = computed<boolean>(
    () => isAvailableSalesforceApiFeature.value && isLeadSfSync.value,
  );
  const disableConnectCheckbox = computed<boolean>(() => isEdit() || !enabledInputSfSync.value);
  const isSfTaskSync = computed<boolean>(() => !disableConnectCheckbox.value && checkedIsSfSync());
  const disableConnectToolChipText = computed<string | null>(() => {
    if (!isLeadSfSync.value) {
      return i18n.t('sfActionTask.checkbox.toolChip.disabledText.notSynced');
    }
    if (isEdit()) {
      return i18n.t('sfActionTask.checkbox.toolChip.disabledText.isEdit');
    }
    return null;
  });

  return {
    isAvailableSalesforceApiFeature,
    isLeadSfSync,
    enabledInputSfSync,
    disableConnectCheckbox,
    isSfTaskSync,
    disableConnectToolChipText,
  };
};

export {
  useSfFeature,
  useSfSync,
  useSfTaskSync,
};

